import React from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  useTheme,
  Paper,
  Avatar,
  Grid,
  Chip,
} from "@mui/material";
import {
  Chat,
  Security,
  KeyboardArrowRight,
  CheckCircle,
  ShoppingCart,
  Bolt,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAuthUser from "./hooks/useAuthUser";
import Footer from "./Footer";
import AiDashboardImage from "../assets/images/ai-dashboard.jpg";
import HeroImage from "../assets/images/hero.jpg";

const Landing: React.FC = () => {
  const navigate = useNavigate();
  useAuthUser();
  const theme = useTheme();

  const features = [
    {
      icon: <Chat />,
      title: "WhatsApp Chatbots",
      description:
        "Create intelligent AI-powered chatbots for your WhatsApp Business account instantly without coding.",
    },
    {
      icon: <ShoppingCart />,
      title: "Connectors",
      description:
        "Seamlessly integrate with your business tools. Starting with Shopify—more connectors coming soon.",
    },
    {
      icon: <Bolt />,
      title: "Smart Automation",
      description:
        "Automate responses, lead generation, and customer support workflows efficiently with AI.",
    },
    {
      icon: <Security />,
      title: "Enterprise Security",
      description:
        "Bank-level encryption and compliance with WhatsApp Business security standards.",
    },
  ];

  const benefits = [
    "80% reduction in customer support costs",
    "Instant responses to customer inquiries 24/7",
    "Connect with 2B+ WhatsApp users worldwide",
    "Seamless integration with your existing tools",
    "Scale from startup to enterprise seamlessly",
    "Deploy in minutes with no coding required",
  ];

  const useCases = [
    {
      title: "E-Commerce Sales",
      description:
        "Product recommendations and instant order processing through WhatsApp.",
      icon: "🛒",
    },
    {
      title: "Lead Generation",
      description:
        "Qualify leads automatically and schedule follow-ups with potential customers.",
      icon: "📱",
    },
    {
      title: "Customer Support",
      description:
        "Provide 24/7 support and resolve customer issues instantly.",
      icon: "🎯",
    },
    {
      title: "Appointment Booking",
      description:
        "Allow customers to book services directly through WhatsApp.",
      icon: "📅",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box sx={{ flex: 1 }}>
        {/* Hero Section */}
        <Box
        sx={{
          background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, sm: 8, md: 12 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)
            `,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack
                spacing={4}
                sx={{
                  textAlign: { xs: "center", md: "left" },
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box>
                  <Paper
                    elevation={0}
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 1,
                      borderRadius: "50px",
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      ✨ Welcome to the Future
                    </Typography>
                  </Paper>
                </Box>

                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: "white",
                        fontWeight: 900,
                        mb: 1,
                        fontSize: { xs: "2.5rem", sm: "3rem", md: "4rem" },
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      MeraAI
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.85)",
                        fontWeight: 600,
                        fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      Your Personal AI Agent
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h2"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      mb: 2,
                      fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    Transform Your Business with WhatsApp AI Chatbots
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 400,
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                      maxWidth: "700px",
                    }}
                  >
                    Connect your WhatsApp Business account, integrate Shopify,
                    and deploy intelligent chatbots in minutes—not weeks.
                    Automate support, generate leads, and scale your business.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/settings")}
                  sx={{
                    backgroundColor: "white",
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    py: 1.75,
                    px: 4,
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
                    },
                    width: "fit-content",
                  }}
                  endIcon={<KeyboardArrowRight />}
                >
                  Get Started
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "white",
                  }}
                >
                  <Box sx={{ display: "flex", gap: -1 }}>
                    {[1, 2, 3].map((i) => (
                      <Avatar
                        key={i}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                          border: "2px solid white",
                          ml: i > 1 ? "-8px" : 0,
                        }}
                      >
                        {String.fromCharCode(64 + i)}
                      </Avatar>
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Trusted by 500+ teams
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: "500px",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                }}
              >
                <Box
                  component="img"
                  src={HeroImage}
                  alt="WhatsApp Chatbot Business"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 6, sm: 8, md: 12 },
          backgroundColor: "background.default",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "500px",
            height: "500px",
            background:
              "radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={8} sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: "text.primary",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Powerful Features
              </Typography>
              <Box
                sx={{
                  width: "60px",
                  height: "4px",
                  background:
                    "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                  borderRadius: "2px",
                  mx: "auto",
                  mb: 3,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  maxWidth: "600px",
                  mx: "auto",
                  fontWeight: 400,
                  fontSize: "1.1rem",
                }}
              >
                Build, deploy, and scale WhatsApp chatbots with ease
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <Box
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      p: 3,
                      borderRadius: 2,
                      background: "rgba(255, 255, 255, 0.5)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid",
                      borderColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        transform: "translateY(-12px)",
                        background: "rgba(255, 255, 255, 0.8)",
                        boxShadow: "0 20px 40px rgba(37, 99, 235, 0.15)",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "20px",
                        background:
                          "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        boxShadow: "0 10px 30px rgba(37, 99, 235, 0.2)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1) rotate(5deg)",
                          boxShadow: "0 15px 40px rgba(37, 99, 235, 0.3)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: "2.5rem",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {feature.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 6, sm: 8, md: 12 },
          backgroundColor: "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 300, md: 400 },
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                }}
              >
                <Box
                  component="img"
                  src={AiDashboardImage}
                  alt="AI Analytics Dashboard"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      color: "text.primary",
                    }}
                  >
                    Why Choose Our Platform?
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      mb: 3,
                    }}
                  >
                    Transform your customer interactions with AI-powered agents
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  {benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <CheckCircle
                          sx={{ color: "white", fontSize: "1.2rem" }}
                        />
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{ color: "text.primary" }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/chat")}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                  endIcon={<KeyboardArrowRight />}
                >
                  Get Started
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Use Cases Section */}
      <Box
        sx={{
          py: { xs: 6, sm: 8, md: 12 },
          backgroundColor: "background.default",
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={8}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: "text.primary",
                }}
              >
                Use Cases
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  maxWidth: "600px",
                  mx: "auto",
                  fontWeight: 400,
                }}
              >
                See how teams across industries are using our platform
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {useCases.map((useCase, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "3rem",
                          mb: 2,
                        }}
                      >
                        {useCase.icon}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        {useCase.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Connectors Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          backgroundColor: "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={8}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: "text.primary",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Powerful Integrations
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  maxWidth: "600px",
                  mx: "auto",
                  fontWeight: 400,
                  fontSize: "1.1rem",
                }}
              >
                Connect with your favorite business tools. More connectors
                coming soon.
              </Typography>
            </Box>

            <Grid container spacing={4} alignItems="stretch">
              {/* Shopify Connector */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    p: 3,
                    transition: "all 0.3s ease",
                    border: "2px solid",
                    borderColor: "divider",
                    height: "100%",
                    position: "relative",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 8px 24px rgba(37, 99, 235, 0.15)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Chip
                    label="Coming Soon"
                    size="small"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: "background.paper",
                      fontWeight: 600,
                    }}
                  />
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      background:
                        "linear-gradient(135deg, #96c93d 0%, #7db934 100%)",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src="https://img.icons8.com/?size=100&id=uSHYbs6PJfMT&format=png&color=000000"
                      alt="Shopify"
                      sx={{
                        width: "60%",
                        height: "60%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Shopify
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sync your store inventory and orders directly with
                      WhatsApp
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Appointo Connector */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    p: 3,
                    transition: "all 0.3s ease",
                    border: "2px solid",
                    borderColor: "divider",
                    height: "100%",
                    position: "relative",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 8px 24px rgba(37, 99, 235, 0.15)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Chip
                    label="Coming Soon"
                    size="small"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: "background.paper",
                      fontWeight: 600,
                    }}
                  />
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      background:
                        "linear-gradient(135deg, #158a06ff 0%, #158a06ff 100%)",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src="https://img.icons8.com/ios-filled/50/FFFFFF/tear-off-calendar.png"
                      alt="Appointo"
                      sx={{
                        width: "60%",
                        height: "60%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Appointo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage appointments and bookings seamlessly through
                      WhatsApp
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Many More Coming */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    py: { xs: 4, md: 6 },
                    background:
                      "linear-gradient(135deg, #2563eb08 0%, #7c3aed08 100%)",
                    borderRadius: 3,
                    border: "2px dashed",
                    borderColor: "primary.light",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #2563eb12 0%, #7c3aed12 100%)",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      background:
                        "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    🚀
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: "text.primary",
                    }}
                  >
                    Many More to Come
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    We're integrating with more business tools to enhance your
                    WhatsApp automation capabilities.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)
            `,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="md">
          <Stack
            spacing={4}
            sx={{
              textAlign: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                Ready to Automate Your Business?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 400,
                }}
              >
                Join hundreds of businesses already using our platform to
                deliver exceptional customer experiences through WhatsApp.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/chat")}
              sx={{
                backgroundColor: "white",
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: "1.1rem",
                py: 2,
                px: 5,
                borderRadius: 2,
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
                },
              }}
              endIcon={<KeyboardArrowRight />}
            >
              Start Building Now
            </Button>
          </Stack>
        </Container>
      </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default Landing;
